import { cache } from "react";
import { fetcher } from "@/lib/api";
import { cfImageUrl, type CfImageVariant } from "@/lib/cloudflare-image";
import type { Locale } from "@/lib/i18n";

// ── Types ──────────────────────────────────────────────────────────────────

export type CourseCategory =
  | "competitive-programming"
  | "development"
  | "ai-ml"
  | "ui-ux-design";

export type CourseDeliveryType =
  | "live"
  | "recorded"
  | "hybrid"
  | "bootcamp"
  | "workshop"
  | "institutional";

export type CourseType =
  | "course"
  | "bootcamp"
  | "workshop"
  | "institutional_program";

export type DifficultyLevel =
  | "beginner"
  | "intermediate"
  | "advanced"
  | "all_levels";

export type CourseFaqItem = {
  question_en: string;
  question_bn?: string;
  answer_en: string;
  answer_bn?: string;
};

export type CourseInstructorSocialLinks = {
  website?: string;
  linkedin?: string;
  github?: string;
  youtube?: string;
  twitter?: string;
};

export type CourseInstructor = {
  name: string;
  name_en?: string;
  name_bn?: string;
  bio_en?: string;
  bio_bn?: string;
  avatarImageId?: string;
  title_en?: string;
  title_bn?: string;
  socialLinks?: CourseInstructorSocialLinks;
};

export type CourseInstructorRole = "lead" | "support";

export type CourseInstructorEntry = CourseInstructor & {
  role: CourseInstructorRole;
  sortOrder?: number;
};

export type CourseLessonDripRule = {
  type: "immediate" | "days_after_enrollment" | "specific_date";
  daysAfterEnrollment?: number;
  specificDate?: string;
};

/**
 * A live lesson's schedule as the public course read carries it (`SEC-CRS01`).
 * The join link, the recordings and the attendance come from the lesson read
 * (`features/lessons/api.ts`), behind the learner's access.
 */
export type CourseLessonLive = {
  meetingProvider: "google_meet" | "zoom" | "other";
  /** ISO 8601 from API */
  startTime: string;
  endTime: string;
  timezone: string;
  joinEarlyMinutes?: number;
  hasRecording?: boolean;
};

export type CourseLesson = {
  _id: string;
  title_en: string;
  title_bn?: string;
  order: number;
  type: "video" | "text" | "resource" | "quiz" | "assignment" | "live" | "coding_exercise";
  freePreview: boolean;
  duration?: number;
  quizMode?: "embedded" | "standalone";
  standaloneQuizId?: string;
  standaloneQuizCompletion?: "submit" | "pass";
  standaloneQuiz?: {
    _id: string;
    title: string;
    slug: string;
    passingPercentage?: number;
    maxAttempts?: number;
  };
  /** Present on live lessons from course detail API */
  live?: CourseLessonLive;
  dripRule?: CourseLessonDripRule;
  // Live class metadata (for sidebar indicators)
  liveStatus?: "scheduled" | "join_early" | "live" | "ended" | "recording_available";
};

export type CourseModule = {
  _id: string;
  title_en: string;
  title_bn?: string;
  order: number;
  lessons: CourseLesson[];
};

export type CoursePrice = {
  list: number;
  sale?: number;
  currency: string;
  isFree: boolean;
};

export type PricingPlanFeature = {
  text_en: string;
  text_bn?: string;
};

export type PricingPlanAccessScopeType =
  | "full"
  | "phases"
  | "modules"
  | "cross_course";

export type PricingPlan = {
  _id: string;
  slug?: string;
  name_en: string;
  name_bn?: string;
  /** Short plan subtitle (from admin description). */
  description_en?: string;
  description_bn?: string;
  price: CoursePrice;
  /** The plan's USD pair (`GR-CRS01`): what a non-Bangladesh account pays by card; absent when not sold in USD. */
  priceUsd?: number;
  salePriceUsd?: number;
  /** Which recorded video set the plan carries (founder decision D5). */
  language?: "en" | "bn";
  accessSummary_en?: string;
  accessSummary_bn?: string;
  features: PricingPlanFeature[];
  badge_en?: string;
  badge_bn?: string;
  isDefault: boolean;
  /** ISO string from public API; optional display on course detail. */
  cohortStartAt?: string;
  /** ISO string from public API; optional sprint countdown display. */
  sprintEndsAt?: string;
  seatStatus?: {
    total: number | null;
    taken: number | null;
    remaining: number | null;
  };
  /** From public API; used for legacy-equivalent fallback when `isDefault` is absent. */
  planType?: "single" | "tier" | "bundle";
  /** From public API; only `type` is exposed publicly. */
  accessScope?: { type: PricingPlanAccessScopeType };
  upgradeableTo: string[];
  sortOrder: number;
};

export type CourseContentVisibility =
  | "full"
  | "summary_only"
  | "enrolled_only"
  | "hidden";

export type CourseThumbnailMediaType = "image" | "video";
export type CourseThumbnailVideoStatus =
  | "queued"
  | "processing"
  | "encoding"
  | "ready"
  | "failed"
  | "unknown";

export type CourseShowcaseVisibility = {
  showQuickValueStrip?: boolean;
  showWhatYouWillLearn?: boolean;
  /** When false, public page hides the Course Content block, hero module/lesson counts, and the yellow callout. Default: true. */
  showCurriculum?: boolean;
  showLearningJourney?: boolean;
  showToolsAndTechnologies?: boolean;
  showProjectsYouWillBuild?: boolean;
  showPracticeAndAssessments?: boolean;
  showCourseDescription?: boolean;
  showWhatsIncluded?: boolean;
  showLearningSupport?: boolean;
  showCertificateAndCareerOutcomes?: boolean;
  /** Legacy-flow sections — explicit false hides even when CMS content exists; omitted defaults to visible. */
  showInstructorSection?: boolean;
  showWhoIsThisFor?: boolean;
  showFaqSection?: boolean;
  showPricingSection?: boolean;
  courseContentVisibility?: CourseContentVisibility;
  customized?: boolean;
  lastDeliveryType?: CourseDeliveryType;
};

export type CourseQuickValueIconKey =
  | "video"
  | "code"
  | "award"
  | "users"
  | "calendar"
  | "infinity"
  | "trophy"
  | "bookOpen"
  | "clock"
  | "certificate"
  | "fileText"
  | "messageCircle"
  | "briefcase"
  | "rocket"
  | "target"
  | "graduationCap"
  | "sparkles"
  | "globe"
  | "shield"
  | "zap"
  | "layers"
  | "database"
  | "terminal"
  | "gitBranch"
  | "headphones"
  | "handshake";

export type CourseLearningJourneyPhaseType =
  | "core"
  | "project"
  | "assessment"
  | "live"
  | "bonus";

export type CoursePresentationToolCategory =
  | "language"
  | "framework"
  | "database"
  | "tool"
  | "platform"
  | "library"
  | "other";

export type CoursePresentationToolIconKey =
  | "javascript"
  | "typescript"
  | "react"
  | "nextjs"
  | "nodejs"
  | "express"
  | "mongodb"
  | "git"
  | "github"
  | "docker"
  | "cicd"
  | "openai"
  | "database"
  | "cloud"
  | "terminal"
  | "code"
  | "default";

export type CoursePresentationProjectDifficulty =
  | "beginner"
  | "intermediate"
  | "advanced";

export type CoursePresentationProjectType =
  | "mini_project"
  | "capstone"
  | "portfolio"
  | "case_study";

// ── Practice & Assessments types ─────────────────────────────────────────────

export type CoursePresentationAssessmentType =
  | "assignment"
  | "quiz"
  | "contest"
  | "problem_set"
  | "review";

export type CoursePresentationAssessmentStats = {
  codingProblems?: number;
  assignments?: number;
  quizzes?: number;
  contests?: number;
  liveReviewSessions?: number;
};

export type CoursePresentationAssessmentItem = {
  order: number;
  type: CoursePresentationAssessmentType;
  title_en: string;
  title_bn?: string;
  description_en?: string;
  description_bn?: string;
};

// ── What's Included types ─────────────────────────────────────────────────────

export type CoursePresentationBenefitType =
  | "recordings"
  | "live_classes"
  | "certificate"
  | "community"
  | "mentor_support"
  | "assignment_review"
  | "resources"
  | "lifetime_access"
  | "career_guidance"
  | "custom";

export type CoursePresentationBenefitItem = {
  order: number;
  type: CoursePresentationBenefitType;
  label_en: string;
  label_bn?: string;
  description_en?: string;
  description_bn?: string;
};

// ── Learning Support types ────────────────────────────────────────────────────

export type CoursePresentationSupportType =
  | "community"
  | "mentor_support"
  | "live_support"
  | "review_session"
  | "office_hour"
  | "doubt_solving"
  | "custom";

export type CoursePresentationSupportItem = {
  order: number;
  type: CoursePresentationSupportType;
  label_en: string;
  label_bn?: string;
  description_en?: string;
  description_bn?: string;
  availability?: string;
};

// ── Certificate & Career Outcomes types ───────────────────────────────────────

export type CoursePresentationCertificateType =
  | "completion"
  | "achievement"
  | "verified";

export type CoursePresentationCertificate = {
  enabled?: boolean;
  type?: CoursePresentationCertificateType;
  previewImageId?: string;
  description_en?: string;
  description_bn?: string;
};

export type CourseBilingualItem = {
  en: string;
  bn?: string;
};

// ── CoursePresentation ────────────────────────────────────────────────────────

export type CoursePresentation = {
  visibility?: {
    showQuickValueStrip?: boolean;
    showWhatYouLearn?: boolean;
    showLearningJourney?: boolean;
    showToolsTechnologies?: boolean;
    showProjects?: boolean;
    showPracticeAssessments?: boolean;
    showDescription?: boolean;
    courseContentVisibility?:
      | "show_full"
      | "show_summary"
      | "enrolled_only"
      | "hide_public";
    showIncludedBenefits?: boolean;
    showLearningSupport?: boolean;
    showCertificateCareer?: boolean;
  };
  quickValueStrip?: {
    items?: Array<{
      iconKey: CourseQuickValueIconKey;
      label_en: string;
      label_bn?: string;
      order: number;
    }>;
  };
  /** Sidebar "This course includes" override — empty list ⇒ auto-computed from curriculum. */
  sidebarIncludes?: {
    items?: Array<{
      iconKey: CourseQuickValueIconKey;
      label_en: string;
      label_bn?: string;
      order: number;
    }>;
  };
  learningJourney?: {
    title_en?: string;
    title_bn?: string;
    subtitle_en?: string;
    subtitle_bn?: string;
    phases?: Array<{
      order: number;
      type: CourseLearningJourneyPhaseType;
      title_en: string;
      title_bn?: string;
      outcome_en?: string;
      outcome_bn?: string;
      topics?: Array<{ en: string; bn?: string }>;
      practice_en?: string;
      practice_bn?: string;
      durationLabel?: string;
      badge_en?: string;
      badge_bn?: string;
    }>;
  };
  toolsAndTechnologies?: {
    items?: Array<{
      order: number;
      name: string;
      category: CoursePresentationToolCategory;
      iconKey?: CoursePresentationToolIconKey;
      iconImageId?: string;
    }>;
  };
  projects?: {
    items?: Array<{
      order: number;
      title_en: string;
      title_bn?: string;
      description_en?: string;
      description_bn?: string;
      thumbnailImageId?: string;
      difficulty: CoursePresentationProjectDifficulty;
      type: CoursePresentationProjectType;
      technologies?: string[];
      demoUrl?: string;
      isCapstone?: boolean;
    }>;
  };
  practiceAndAssessments?: {
    stats?: CoursePresentationAssessmentStats;
    items?: CoursePresentationAssessmentItem[];
  };
  includedBenefits?: {
    items?: CoursePresentationBenefitItem[];
  };
  learningSupport?: {
    items?: CoursePresentationSupportItem[];
  };
  certificateAndCareer?: {
    certificate?: CoursePresentationCertificate;
    careerRoles?: CourseBilingualItem[];
    careerSkills?: CourseBilingualItem[];
    portfolioOutcomes?: CourseBilingualItem[];
  };
};

function mapLegacyCourseContentVisibility(
  value:
    | "show_full"
    | "show_summary"
    | "enrolled_only"
    | "hide_public"
    | undefined,
): CourseContentVisibility | undefined {
  switch (value) {
    case "show_full":
      return "full";
    case "show_summary":
      return "summary_only";
    case "enrolled_only":
      return "enrolled_only";
    case "hide_public":
      return "hidden";
    default:
      return undefined;
  }
}

function normalizeShowcaseVisibility(course: Course): CourseShowcaseVisibility | undefined {
  const legacy = course.coursePresentation?.visibility;
  const fromLegacy: CourseShowcaseVisibility | undefined = legacy
    ? {
        showQuickValueStrip: legacy.showQuickValueStrip,
        showWhatYouWillLearn: legacy.showWhatYouLearn,
        showLearningJourney: legacy.showLearningJourney,
        showToolsAndTechnologies: legacy.showToolsTechnologies,
        showProjectsYouWillBuild: legacy.showProjects,
        showPracticeAndAssessments: legacy.showPracticeAssessments,
        showCourseDescription: legacy.showDescription,
        showWhatsIncluded: legacy.showIncludedBenefits,
        showLearningSupport: legacy.showLearningSupport,
        showCertificateAndCareerOutcomes: legacy.showCertificateCareer,
        courseContentVisibility: mapLegacyCourseContentVisibility(
          legacy.courseContentVisibility,
        ),
      }
    : undefined;

  if (!fromLegacy && !course.showcaseVisibility) return undefined;

  return {
    ...(fromLegacy ?? {}),
    ...(course.showcaseVisibility ?? {}),
  };
}

/** GET /v1/courses/:slug/pricing-plans wire shape (backend `serializePublicPlan`). */
type PublicPricingPlanWire = {
  id: string;
  name: { en: string; bn?: string };
  slug?: string;
  description?: { en: string; bn?: string };
  features?: Array<{ en: string; bn?: string }>;
  /** List price (number), not `{ list, sale }`. */
  price: number;
  salePrice?: number;
  priceUsd?: number;
  salePriceUsd?: number;
  language?: string;
  currency?: string;
  displayOrder?: number;
  badge?: { en?: string; bn?: string };
  /** ISO 8601 from public API. */
  cohortStartAt?: string;
  /** ISO 8601 from public API. */
  sprintEndsAt?: string;
  seatStatus?: {
    total: number | null;
    taken: number | null;
    remaining: number | null;
  };
  upgradeableTo?: string[];
  accessSummary?: { label_en: string; label_bn: string };
  isDefault?: boolean;
  planType?: "single" | "tier" | "bundle";
  accessScope?: { type: PricingPlanAccessScopeType };
};

function isCoursePriceObject(v: unknown): v is CoursePrice {
  return (
    typeof v === "object" &&
    v !== null &&
    "list" in v &&
    typeof (v as CoursePrice).list === "number" &&
    "currency" in v &&
    typeof (v as CoursePrice).currency === "string" &&
    "isFree" in v &&
    typeof (v as CoursePrice).isFree === "boolean"
  );
}

/**
 * Maps public pricing-plan API payloads into the `PricingPlan` shape used by the web UI.
 * The API exposes `price` as a number (list) and `salePrice`; the UI expects nested `CoursePrice`.
 */
export function normalizePublicPricingPlanFromApi(raw: unknown): PricingPlan {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid pricing plan payload");
  }
  const w = raw as Record<string, unknown>;

  if (isCoursePriceObject(w.price)) {
    return raw as PricingPlan;
  }

  const wire = raw as PublicPricingPlanWire;
  const list =
    typeof wire.price === "number" && Number.isFinite(wire.price) ? wire.price : 0;
  const saleRaw =
    typeof wire.salePrice === "number" && Number.isFinite(wire.salePrice)
      ? wire.salePrice
      : undefined;
  const currency = wire.currency ?? "BDT";
  const sale =
    saleRaw !== undefined && saleRaw < list ? saleRaw : undefined;
  const isFree = list === 0 && (sale === undefined || sale === 0);

  const coursePrice: CoursePrice = {
    list,
    ...(sale !== undefined ? { sale } : {}),
    currency,
    isFree,
  };

  const nameEn = wire.name?.en ?? "Plan";
  const nameBn = wire.name?.bn ?? nameEn;
  const features: PricingPlanFeature[] = Array.isArray(wire.features)
    ? wire.features.map((f) => ({
        text_en: f.en,
        ...(f.bn ? { text_bn: f.bn } : {}),
      }))
    : [];

  const rid = raw as Record<string, unknown>;
  const planId =
    typeof rid["id"] === "string"
      ? rid["id"]
      : rid["_id"] != null
        ? String(rid["_id"])
        : typeof wire.id === "string"
          ? wire.id
          : "";

  const descEn = wire.description?.en?.trim();
  const descBn = wire.description?.bn?.trim();

  return {
    _id: planId,
    slug: wire.slug,
    name_en: nameEn,
    name_bn: nameBn,
    ...(descEn ? { description_en: descEn } : {}),
    ...(descBn ? { description_bn: descBn } : {}),
    price: coursePrice,
    ...(typeof wire.priceUsd === "number" ? { priceUsd: wire.priceUsd } : {}),
    ...(typeof wire.salePriceUsd === "number" ? { salePriceUsd: wire.salePriceUsd } : {}),
    ...(wire.language === "en" || wire.language === "bn" ? { language: wire.language } : {}),
    accessSummary_en: wire.accessSummary?.label_en,
    accessSummary_bn: wire.accessSummary?.label_bn,
    features,
    badge_en: wire.badge?.en,
    badge_bn: wire.badge?.bn,
    isDefault: Boolean(wire.isDefault),
    ...(wire.planType ? { planType: wire.planType } : {}),
    ...(wire.accessScope?.type
      ? { accessScope: { type: wire.accessScope.type } }
      : {}),
    upgradeableTo: Array.isArray(wire.upgradeableTo) ? wire.upgradeableTo.map(String) : [],
    sortOrder: typeof wire.displayOrder === "number" ? wire.displayOrder : 0,
    ...(typeof wire.cohortStartAt === "string" && wire.cohortStartAt
      ? { cohortStartAt: wire.cohortStartAt }
      : {}),
    ...(typeof wire.sprintEndsAt === "string" && wire.sprintEndsAt
      ? { sprintEndsAt: wire.sprintEndsAt }
      : {}),
    ...(wire.seatStatus ? { seatStatus: wire.seatStatus } : {}),
  };
}

export type PublicCourseLaunchState =
  | "pre_launch"
  | "launching_soon"
  | "live"
  | "ongoing"
  | "concluded"
  | "unknown";

export type PinnedAnnouncementPublic = {
  id: string;
  title: { en: string; bn?: string };
  body: { en: string; bn?: string };
  createdAt: string;
};

export type Course = {
  _id: string;
  title_en: string;
  title_bn?: string;
  slug: string;
  /** True only when backend returned an internal draft/unpublished detail preview. */
  isPreviewMode?: boolean;
  short_description_en?: string;
  short_description_bn?: string;
  long_description_en?: string;
  long_description_bn?: string;
  category: CourseCategory;
  delivery_type: CourseDeliveryType;
  course_type: CourseType;
  thumbnailImageId?: string | null;
  thumbnailProvider: "cloudflare";
  thumbnailMediaType?: CourseThumbnailMediaType;
  thumbnailVideoAssetId?: string | null;
  thumbnailVideoProvider?: "bunny" | null;
  thumbnailVideoLibraryId?: string | null;
  thumbnailVideoId?: string | null;
  thumbnailVideoStatus?: CourseThumbnailVideoStatus;
  thumbnailVideoEmbedUrl?: string;
  /** Legacy single instructor; optional when using `instructors` or when omitted on the course. */
  instructor?: CourseInstructor;
  /** Multi-instructor roster with lead/support roles (canonical when present). */
  instructors?: CourseInstructorEntry[];
  price: CoursePrice;
  modules: CourseModule[];

  // ── Enrichment fields ─────────────────────────────────────────────────────
  whatYouWillLearn_en?: string[];
  whatYouWillLearn_bn?: string[];
  whoIsThisFor_en?: string[];
  whoIsThisFor_bn?: string[];
  requirements_en?: string[];
  requirements_bn?: string[];
  highlights_en?: string[];
  highlights_bn?: string[];
  faq?: CourseFaqItem[];
  difficultyLevel?: DifficultyLevel;
  language?: string;
  totalDurationMinutes?: number;
  lastUpdatedContent?: string;

  // ── SEO ───────────────────────────────────────────────────────────────────
  seoTitle_en?: string;
  seoTitle_bn?: string;
  seoDescription_en?: string;
  seoDescription_bn?: string;
  seoKeywords?: string[];

  // ── Enrollment settings ───────────────────────────────────────────────────
  certificateEnabled?: boolean;
  /** Days a purchase keeps access; absent means lifetime (`GR-TRU01` reads it before saying "Lifetime access"). */
  accessDurationDays?: number | null;

  // ── Refund / guarantee note (bilingual, optional, per-course) ─────────────
  refundPolicyText_en?: string;
  refundPolicyText_bn?: string;

  // ── Homepage feature flag ─────────────────────────────────────────────────
  featured?: boolean;
  featuredRank?: number;

  // ── Thumbnail template override (optional; backend field may be absent) ──
  useTemplateThumbnail?: boolean;

  // ── Phases (B5 — sequential unlock) ──────────────────────────────────────
  phases?: Array<{
    id: string;
    name: { en: string; bn?: string };
    moduleIds: string[];
    prerequisitePhaseId?: string;
    order: number;
  }>;

  /**
   * `LN-HUB01`, docs/35 §4: this course is a Progsity Learn track, so its home
   * is `/learn/:trackSlug` and the catalogue card links there. Optional, and
   * absent on every course that is not one.
   */
  learnTrack?: boolean;
  trackSlug?: string | null;

  /** e.g. "Road to FAANG · Phase 1" — public hero badge. */
  programRelation?: string;

  eligibility?: {
    enabled: boolean;
    quizSlug: string;
    quizTitle: string;
  } | null;

  /** Public course detail — cohort / launch UX (computed server-side). */
  launchState?: PublicCourseLaunchState;
  nextCohortStartAt?: string | null;
  daysUntilStart?: number | null;
  pinnedAnnouncement?: PinnedAnnouncementPublic | null;

  /**
   * Active pricing-plan summary, on list, card and detail payloads
   * (`docs/07` Courses (public)): 0 plans = the legacy `price` pair,
   * 1 plan = that plan's price, 2+ plans = "From `fromPrice`". Optional so a
   * payload cached before the field shipped still typechecks.
   */
  planCount?: number;
  fromPrice?: number | null;
  /** Lowest USD price across the plans that carry one (`GR-CRS01`). */
  fromPriceUsd?: number | null;
  /** Counted off the lessons server-side (`GR-CRS01`): what the course is written in, and how many lessons carry a video. */
  languageAvailability?: { lessons: number; textEn: number; textBn: number; video: number };

  // ── Showcase / public course presentation ────────────────────────────────
  showcaseVisibility?: CourseShowcaseVisibility;
  coursePresentation?: CoursePresentation;

  createdAt: string;
  updatedAt: string;
};

export type CourseEligibilityStatus = {
  required: boolean;
  passed: boolean;
  quiz: {
    slug: string;
    title: string;
    attemptUrl: string;
  } | null;
  lastAttempt: {
    sessionId: string;
    percentage: number;
    passed: boolean;
    completedAt: string;
  } | null;
};

/** Mirrors backend `GET /v1/courses?sort=` — server-side ordering. */
export type CoursesListSort = "newest" | "price_asc" | "price_desc";

export type CoursesListParams = {
  /** Category slug — accepts any string slug (API-driven) or legacy CourseCategory literals */
  category?: CourseCategory | (string & {}) | "";
  delivery_type?: CourseDeliveryType | "";
  /** Public “Live + Recorded” filter — API uses `$in` (e.g. `['hybrid','live']`). */
  delivery_types?: CourseDeliveryType[];
  difficultyLevel?: DifficultyLevel | "";
  search?: string;
  sort?: CoursesListSort;
  page?: number;
  limit?: number;
};

export type CoursesListResponse = {
  courses: Course[];
  page: number;
  limit: number;
  total: number;
};

// ── Helpers ────────────────────────────────────────────────────────────────

export function getThumbnailUrl(
  imageId: string | null | undefined,
  variant: CfImageVariant = "card",
): string | null {
  if (!imageId) return null;
  const url = cfImageUrl(imageId, variant);
  return url || null;
}

/**
 * Resolution rules for a course card thumbnail:
 *  - No `thumbnailImageId` → fallback
 *  - `useTemplateThumbnail === true` (optional admin override) → fallback
 *  - Otherwise → Cloudflare Images `variant` URL (default "card")
 *
 * Returning a `null` URL signals the caller to render the shared
 * `CourseThumbnailFallback` component instead of an `<Image/>`.
 */
export function resolveCourseThumbnail(
  course: Pick<Course, "thumbnailImageId"> & { useTemplateThumbnail?: boolean },
  variant: CfImageVariant = "card",
): { url: string | null; useFallback: boolean } {
  if (course.useTemplateThumbnail) return { url: null, useFallback: true };
  const url = getThumbnailUrl(course.thumbnailImageId, variant);
  return { url, useFallback: !url };
}

/**
 * Returns the localized version of a field pair.
 * Falls back to the English value if the localized value is absent.
 */
export function getLocalizedField(
  field_en: string | undefined,
  field_bn: string | undefined,
  locale: Locale,
): string | undefined {
  if (locale === "bn") {
    return field_bn?.trim() ? field_bn : field_en;
  }
  return field_en;
}

/**
 * Returns the localized string array.
 * Falls back to EN array if BN array is absent or empty.
 */
export function getLocalizedArray(
  arr_en: string[] | undefined,
  arr_bn: string[] | undefined,
  locale: Locale,
): string[] {
  if (locale === "bn" && arr_bn && arr_bn.length > 0) return arr_bn;
  return arr_en ?? [];
}

/**
 * Returns the localized instructor display name.
 * name_en is already normalized from the backend (falls back to `name`).
 */
export function getInstructorName(
  instructor: CourseInstructor,
  locale: Locale,
): string {
  if (locale === "bn" && instructor.name_bn?.trim()) return instructor.name_bn;
  return instructor.name_en ?? instructor.name;
}

const PLACEHOLDER_INSTRUCTOR_NAMES = new Set(["TBA", "TBD", "N/A"]);

/** True when the instructor name is empty or a generic placeholder (hide on public pages). */
export function isBlankInstructorName(name: string | undefined | null): boolean {
  const t = (name ?? "").trim();
  if (!t) return true;
  return PLACEHOLDER_INSTRUCTOR_NAMES.has(t.toUpperCase());
}

/**
 * Instructors to show on the public course page: prefers `instructors[]`,
 * otherwise a single legacy `instructor` row (as lead) when the name is real.
 */
export function getPublicCourseInstructors(course: Course): CourseInstructorEntry[] {
  const fromList = (course.instructors ?? []).filter(
    (e) => !isBlankInstructorName(e.name_en ?? e.name),
  );
  if (fromList.length > 0) {
    return [...fromList].sort((a, b) => {
      const ra = a.role === "lead" ? 0 : 1;
      const rb = b.role === "lead" ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }
  const leg = course.instructor;
  if (!leg || isBlankInstructorName(leg.name_en ?? leg.name)) return [];
  return [{ ...leg, role: "lead", sortOrder: 0 }];
}

function instructorEntryToCore(entry: CourseInstructorEntry): CourseInstructor {
  return {
    name: entry.name,
    name_en: entry.name_en,
    name_bn: entry.name_bn,
    bio_en: entry.bio_en,
    bio_bn: entry.bio_bn,
    avatarImageId: entry.avatarImageId,
    title_en: entry.title_en,
    title_bn: entry.title_bn,
    socialLinks: entry.socialLinks,
  };
}

export const CATEGORY_LABELS: Record<CourseCategory, string> = {
  "competitive-programming": "Competitive Programming",
  development: "Development",
  "ai-ml": "AI/ML",
  "ui-ux-design": "UI/UX Design",
};

export type PublicCategory = {
  slug: string;
  name_en: string;
  name_bn?: string;
  isActive: boolean;
  courseCount: number;
  featuredInHero?: boolean;
  featuredOrder?: number;
};

export type FeaturedCategory = {
  slug: string;
  name_en: string;
  name_bn?: string;
  featuredOrder?: number;
};

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  all_levels: "All Levels",
};

export const COURSE_CATEGORIES: CourseCategory[] = [
  "competitive-programming",
  "development",
  "ai-ml",
  "ui-ux-design",
];

export const COURSE_DELIVERY_TYPES: CourseDeliveryType[] = [
  "live",
  "recorded",
  "hybrid",
  "bootcamp",
  "workshop",
  "institutional",
];

/** Some endpoints (e.g. pricing plans) may omit numeric fields; use before formatting. */
export function coalesceCourseMoney(value: number | undefined | null): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function formatCourseMoneyEnIn(value: number | undefined | null): string {
  return coalesceCourseMoney(value).toLocaleString("en-IN");
}

// ── API functions ──────────────────────────────────────────────────────────

const OUTLINE_LESSON_FIELDS = [
  "_id",
  "title_en",
  "title_bn",
  "order",
  "type",
  "freePreview",
  "duration",
  "quizMode",
  "standaloneQuizId",
  "standaloneQuizCompletion",
  "standaloneQuiz",
  "dripRule",
  "liveStatus",
] as const satisfies ReadonlyArray<keyof CourseLesson>;

const OUTLINE_LIVE_FIELDS = [
  "meetingProvider",
  "startTime",
  "endTime",
  "timezone",
  "joinEarlyMinutes",
  "hasRecording",
] as const satisfies ReadonlyArray<keyof CourseLessonLive>;

function pickFields<T extends object>(source: T, fields: readonly string[]): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const value = (source as Record<string, unknown>)[field];
    if (value !== undefined) out[field] = value;
  }
  return out as Partial<T>;
}

/**
 * `SEC-CRS01`: one lesson as the course pages may hold it. The API sends an
 * outline since the same ticket; this keeps a stale cached payload, or a
 * later slip in the API, out of the page's props and so out of its RSC
 * payload, where the course page used to carry every quiz answer.
 */
export function toOutlineLesson(lesson: CourseLesson): CourseLesson {
  const out = pickFields(lesson, OUTLINE_LESSON_FIELDS) as CourseLesson;
  if (lesson.live) out.live = pickFields(lesson.live, OUTLINE_LIVE_FIELDS) as CourseLessonLive;
  return out;
}

function toOutlineModule(module: CourseModule): CourseModule {
  return { ...module, lessons: (module.lessons ?? []).map(toOutlineLesson) };
}

/**
 * Normalizes public course detail payloads so SSR never throws on sparse CMS data
 * (e.g. FAQ answers omitted, legacy docs missing `modules` or numeric `price.list`).
 */
export function normalizePublicCourseDetail(course: Course): Course {
  const raw = course.price;
  const list =
    raw && typeof raw.list === "number" && !Number.isNaN(raw.list) ? raw.list : 0;
  const price: Course["price"] = raw
    ? {
        list,
        sale:
          typeof raw.sale === "number" && !Number.isNaN(raw.sale)
            ? raw.sale
            : undefined,
        currency: raw.currency ?? "BDT",
        isFree: Boolean(raw.isFree),
      }
    : { list: 0, currency: "BDT", isFree: true };

  const modules = Array.isArray(course.modules) ? course.modules.map(toOutlineModule) : [];

  const instructors = getPublicCourseInstructors(course);
  const primary = instructors[0];

  return {
    ...course,
    price,
    modules,
    instructors,
    instructor: primary ? instructorEntryToCore(primary) : undefined,
    showcaseVisibility: normalizeShowcaseVisibility(course),
    launchState: course.launchState ?? "unknown",
    nextCohortStartAt:
      course.nextCohortStartAt === undefined ? null : course.nextCohortStartAt,
    daysUntilStart:
      course.daysUntilStart === undefined ? null : course.daysUntilStart,
    pinnedAnnouncement:
      course.pinnedAnnouncement === undefined ? null : course.pinnedAnnouncement,
  };
}

export async function fetchCoursesList(
  params: CoursesListParams
): Promise<CoursesListResponse> {
  const q = new URLSearchParams();
  if (params.category) q.set("category", params.category);
  if (params.delivery_type) q.set("delivery_type", params.delivery_type);
  if (params.delivery_types?.length) {
    q.set("delivery_types", params.delivery_types.join(","));
  }
  if (params.difficultyLevel) q.set("difficultyLevel", params.difficultyLevel);
  if (params.search) q.set("search", params.search);
  if (params.sort) q.set("sort", params.sort);
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  const res = await fetcher<CoursesListResponse>(
    `/v1/courses${qs ? `?${qs}` : ""}`
  );
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

/**
 * Deduplicates the course detail fetch when `generateMetadata` and the page
 * both run in the same RSC request (avoids two identical API round-trips).
 *
 * `SITE-CRS01`: the public read is the same for every visitor (the API adds
 * nothing per viewer without a preview token), so it is cached for 300 s and
 * the course page is prebuilt (ISR). A preview read is never cached. Failures
 * throw `ApiError` with the API's status, so a caller can tell "no such course"
 * (404) from an API that is down (which must not be cached as a 404).
 */
export const fetchCourseBySlug = cache(async function fetchCourseBySlug(
  slug: string,
  previewToken?: string | null,
): Promise<Course> {
  const token = previewToken?.trim();
  const res = await fetcher<{ course: Course }>(`/v1/courses/${slug}`, {
    headers: token ? { "X-Preview-Token": token } : {},
    ...(token ? { cache: "no-store" as const } : { next: { revalidate: 300 } }),
  });
  if (!res.success) throw new Error(res.error?.message);
  return normalizePublicCourseDetail(res.data.course);
});

export async function fetchCoursePricingPlans(
  slug: string,
  previewToken?: string | null,
): Promise<PricingPlan[]> {
  const res = await fetcher<{ plans: unknown[] }>(
    `/v1/courses/${slug}/pricing-plans`,
    {
      headers: previewToken?.trim() ? { "X-Preview-Token": previewToken.trim() } : {},
    },
  );
  if (!res.success) throw new Error(res.error?.message);
  return res.data.plans.map(normalizePublicPricingPlanFromApi);
}

/**
 * `SITE-CRS01`: the public plans read on the server, so a course page's price,
 * plan pick and Enroll arrive in its HTML instead of after the page's script.
 * Cached as long as the page (300 s). `null` on any failure: the provider then
 * reads the plans in the browser, as it always did.
 */
export async function fetchCoursePricingPlansServer(
  slug: string,
): Promise<PricingPlan[] | null> {
  try {
    const res = await fetcher<{ plans: unknown[] }>(`/v1/courses/${slug}/pricing-plans`, {
      next: { revalidate: 300 },
    });
    if (!res.success) return null;
    return res.data.plans.map(normalizePublicPricingPlanFromApi);
  } catch {
    return null;
  }
}

export async function fetchCourseEligibilityStatus(
  idOrSlug: string,
): Promise<CourseEligibilityStatus> {
  const res = await fetcher<CourseEligibilityStatus>(
    `/v1/courses/${encodeURIComponent(idOrSlug)}/eligibility-status`,
  );
  if (!res.success) throw new Error(res.error?.message);
  return res.data;
}

export async function fetchPublicCategories(): Promise<PublicCategory[]> {
  const res = await fetcher<{ categories: PublicCategory[] }>("/v1/categories");
  if (!res.success) return [];
  return res.data.categories;
}

export async function fetchFeaturedCategoriesFromApi(): Promise<FeaturedCategory[]> {
  const res = await fetcher<{ categories: FeaturedCategory[] }>("/v1/categories/featured");
  if (!res.success) return [];
  return res.data.categories;
}

/** Admin-curated homepage Featured Courses — up to 6 cards, Redis-cached 5m. */
export async function fetchFeaturedCoursesFromApi(
  limit = 6,
  init?: RequestInit,
): Promise<Course[]> {
  const q = new URLSearchParams({ limit: String(Math.max(1, Math.min(6, limit))) });
  const res = await fetcher<{ courses: Course[]; limit: number }>(
    `/v1/catalog/featured-courses?${q.toString()}`,
    init,
  );
  if (!res.success) return [];
  return Array.isArray(res.data.courses) ? res.data.courses : [];
}
